<script setup>
import Dialog from 'primevue/dialog'
import Calendar from 'primevue/calendar'
import InputText from 'primevue/inputtext'
import { computed, reactive, ref } from 'vue'
import axios from 'axios'

const props = defineProps({
  cancelRegistrationUrl: { type: String, default: null },
  actualPeriodStartDate: { type: String, default: null },
  creatorId: { type: Number, default: null }
})

const emits = defineEmits(['post-succeed'])

// UI管理フラグ
const isLoading = ref(false)
const displayDialog = ref(false)
const displayResult = ref(false)

const formData = reactive({
  title: '',
  work_date: null,
  scheduled_start_time: null,
  scheduled_end_time: null,
  tax: 0,
  price: null
})
const timeDropdownList = []
for (let hour = 0; hour <= 23; hour++) {
  ;['00', '15', '30', '45'].forEach((i) => {
    timeDropdownList.push(`${hour.toString().padStart(2, '0')}:${i}`)
  })
}

// 実際の時刻に変換 戻り値の各timeはDate型
const timeConvertedFormData = computed(() => {
  let startTime = null
  let endTime = null
  // 計算に必要な値がnullの場合はnullのままにする
  if (formData.work_date) {
    // 時差の影響を受けるためtoISOStringを使えませんでした
    const workDateStr = `${formData.work_date
      .getFullYear()
      .toString()
      .padStart(2, '0')}-${(formData.work_date.getMonth() + 1)
      .toString()
      .padStart(2, '0')}-${formData.work_date
      .getDate()
      .toString()
      .padStart(2, '0')}`
    if (formData.scheduled_start_time) {
      startTime = new Date(`${workDateStr}T${formData.scheduled_start_time}`)
    }
    if (formData.scheduled_end_time) {
      endTime = new Date(`${workDateStr}T${formData.scheduled_end_time}`)
    }
    // start-end逆転時、end+24
    if (startTime && endTime && startTime > endTime) {
      endTime = new Date(endTime.getTime() + 24 * 60 * 60 * 1000)
    }
  }

  return {
    ...formData,
    scheduled_start_time: startTime,
    scheduled_end_time: endTime
  }
})

// 表示用 日付時刻は文字列
const displayedFormData = computed(() => {
  const startTime = timeConvertedFormData.value.scheduled_start_time
  const endTime = timeConvertedFormData.value.scheduled_end_time
  const [startTimeBackDays, endTimeBackDays] = calculateTimeBackDays(
    startTime,
    endTime
  )
  return {
    ...formData,
    work_date: dateToDateString(
      timeConvertedFormData.value.work_date,
      startTimeBackDays
    ),
    scheduled_start_time: dateToTimeString(
      timeConvertedFormData.value.scheduled_start_time,
      startTimeBackDays
    ),
    scheduled_end_time: dateToTimeString(
      timeConvertedFormData.value.scheduled_end_time,
      endTimeBackDays
    )
  }
})

const errorMessage = ref('')
const result = ref([])

const calendar = ref(null)

// 出てくるカレンダーの月を作業明細月に変更
function setupCalendar() {
  calendar.value.currentMonth = new Date(props.actualPeriodStartDate).getMonth()
  calendar.value.currentYear = new Date(
    props.actualPeriodStartDate
  ).getFullYear()
}

// インポートボタン押せる条件
const isSubmitActive = computed(() => {
  if (isLoading.value) return false
  if (formData.work_date == null) return false
  if (formData.tax == null) return false
  if (formData.price == null) return false

  return true
})

const displayedResult = computed(() => {
  let startTimeBackDays = 0
  let endTimeBackDays = 0
  let startTime = null
  let endTime = null
  if (result.value.scheduled_start_time && result.value.scheduled_end_time) {
    startTime = new Date(result.value.scheduled_start_time)
    endTime = new Date(result.value.scheduled_end_time)
    ;[startTimeBackDays, endTimeBackDays] = calculateTimeBackDays(
      startTime,
      endTime
    )
  }
  return {
    ...result.value,
    work_date: dateToDateString(
      new Date(result.value.work_date),
      startTimeBackDays
    ),
    scheduled_start_time: dateToTimeString(startTime, startTimeBackDays),
    scheduled_end_time: dateToTimeString(endTime, endTimeBackDays)
  }
})

function calculateTimeBackDays(startTime, endTime) {
  let startTimeBackDays = 0
  let endTimeBackDays = 0

  if (startTime && endTime && compareTimeOnly(startTime, endTime) >= 0) {
    endTimeBackDays++
  }

  if (startTime && startTime.getHours() < 8) {
    startTimeBackDays++
    endTimeBackDays++
  }

  return [startTimeBackDays, endTimeBackDays]
}

function dateToDateString(date, backDays = 0) {
  if (!date) return ''
  const newDate = new Date(date.getTime())
  if (backDays) {
    newDate.setDate(date.getDate() - backDays)
  }
  const year = newDate.getFullYear()
  const month = newDate.getMonth() + 1
  const day = newDate.getDate()

  return `${year}/${month}/${day}`
}

function dateToTimeString(date, backDays = 0) {
  if (!date) return ''
  let hours = date.getHours().toString().padStart(2, '0')
  if (backDays) {
    hours = (date.getHours() + 24 * backDays).toString()
  }
  const minutes = date.getMinutes().toString().padStart(2, '0')

  return `${hours}:${minutes}`
}

function compareTimeOnly(dateA, dateB) {
  const timeA = dateA.getHours() * 60 + dateA.getMinutes()
  const timeB = dateB.getHours() * 60 + dateB.getMinutes()

  if (timeA < timeB) {
    return -1
  } else if (timeA > timeB) {
    return 1
  } else {
    return 0
  }
}

function closeAndReset() {
  displayDialog.value = false
  resetForm()
  displayResult.value = false
}

function resetForm() {
  formData.title = ''
  formData.work_date = null
  formData.scheduled_start_time = null
  formData.scheduled_end_time = null
  formData.tax = 0
  formData.price = null
}

// 結果画面で上のボタン押して閉じた時
function onHide() {
  if (displayResult.value) {
    resetForm()
    displayResult.value = false
  }
}

async function submit() {
  const url = props.cancelRegistrationUrl
  const data = new FormData()
  data.append('creator_id', props.creatorId)
  data.append('title', timeConvertedFormData.value.title)
  data.append('work_date', timeConvertedFormData.value.work_date)
  data.append(
    'scheduled_start_time',
    timeConvertedFormData.value.scheduled_start_time
  )
  data.append(
    'scheduled_end_time',
    timeConvertedFormData.value.scheduled_end_time
  )
  data.append('tax', timeConvertedFormData.value.tax)
  data.append('price', timeConvertedFormData.value.price)
  const headers = { 'content-type': 'multipart/form-data' }
  isLoading.value = true
  axios.defaults.headers.common['X-CSRF-Token'] = document
    .querySelector('meta[name="csrf-token"]')
    .getAttribute('content')

  try {
    const res = await axios.post(url, data, { headers })
    isLoading.value = false
    displayResult.value = true
    result.value = res.data.actual_detail
    emits('post-succeed')
  } catch (error) {
    console.error('リクエストでエラーが発生しました:', error)
    errorMessage.value = error.response.data.errors.join(' ')
    isLoading.value = false
  }
}
</script>
<template>
  <datalist id="data-list" class="hidden">
    <option v-for="time in timeDropdownList" :key="time" :value="time"></option>
  </datalist>
  <div>
    <div
      class="group flex items-center justify-center rounded-md border border-gray-800 bg-white py-2 pl-3 pr-4 text-sm font-medium text-gray-800 shadow-sm hover:border-yellow-500 hover:bg-yellow-500 hover:text-white sm:w-auto"
      @click="displayDialog = true"
    >
      <button class="flex">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          class="h-5 w-5 pr-1 text-gray-500 group-hover:text-white"
        >
          <path
            fill-rule="evenodd"
            d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z"
            clip-rule="evenodd"
          ></path>
        </svg>
        追加登録
      </button>
    </div>
    <Dialog
      v-model:visible="displayDialog"
      header="追加登録"
      :modal="true"
      :style="{ width: '35rem' }"
      @hide="onHide"
    >
      <vue-element-loading :active="isLoading" />
      <div v-if="!displayResult">
        <div
          v-if="errorMessage"
          id="errors"
          class="mb-2 mt-2 rounded-md bg-red-50 p-4"
        >
          <div class="mt-2 text-sm text-red-700">エラー:{{ errorMessage }}</div>
        </div>
        <div class="mb-2">
          <span
            class="mx-2 inline-flex items-center rounded-md bg-gray-100 px-2.5 py-0.5 text-sm font-medium text-gray-800"
            >案件名</span
          >
          <InputText
            v-model="formData.title"
            type="text"
            class="focus:outline-blue string mt-2 h-10 w-9/12 rounded border p-3 leading-normal"
          />
        </div>
        <div class="mb-2">
          <span
            class="mx-2 inline-flex items-center rounded-md bg-gray-100 px-2.5 py-0.5 text-sm font-medium text-gray-800"
            >作業日</span
          >
          <Calendar
            ref="calendar"
            v-model="formData.work_date"
            class="focus:outline-blue string _p-3 mt-2 h-10 leading-normal"
            @show="setupCalendar"
          />
          <span class="ml-2 text-sm text-gray-400">{{
            displayedFormData.work_date
          }}</span>
        </div>
        <div class="mb-2">
          <span
            class="mx-2 inline-flex items-center rounded-md bg-gray-100 px-2.5 py-0.5 text-sm font-medium text-gray-800"
            >作業予定開始時間</span
          >
          <input
            v-model="formData.scheduled_start_time"
            type="time"
            step="900"
            list="data-list"
            class="focus:outline-blue string mt-2 h-10 rounded border p-3 leading-normal"
          />
          <span class="ml-2 text-sm text-gray-400">{{
            displayedFormData.scheduled_start_time
          }}</span>
        </div>
        <div class="mb-2">
          <span
            class="mx-2 inline-flex items-center rounded-md bg-gray-100 px-2.5 py-0.5 text-sm font-medium text-gray-800"
            >作業予定終了時間</span
          >
          <input
            v-model="formData.scheduled_end_time"
            type="time"
            step="900"
            list="data-list"
            class="focus:outline-blue string mt-2 h-10 rounded border p-3 leading-normal"
          />
          <span class="ml-2 text-sm text-gray-400">{{
            displayedFormData.scheduled_end_time
          }}</span>
        </div>
        <div class="mb-2">
          <span
            class="mx-2 inline-flex items-center rounded-md bg-gray-100 px-2.5 py-0.5 text-sm font-medium text-gray-800"
            >税率</span
          >
          <label v-for="n in [0, 8, 10]" :key="n">
            <input v-model="formData.tax" type="radio" name="tax" :value="n" />
            {{ n }}%
          </label>
        </div>
        <div class="mb-2">
          <span
            class="mx-2 inline-flex items-center rounded-md bg-gray-100 px-2.5 py-0.5 text-sm font-medium text-gray-800"
            >金額</span
          >
          <input
            v-model="formData.price"
            type="number"
            class="focus:outline-blue string mt-2 h-10 rounded border p-3 leading-normal"
          />
        </div>
      </div>
      <div v-if="displayResult">
        <div
          v-if="errorMessage"
          id="errors"
          class="mb-2 mt-2 rounded-md bg-red-50 p-4"
        >
          <div class="mt-2 text-sm text-red-700">エラー:{{ errorMessage }}</div>
        </div>
        <div class="mb-2">キャンセルを登録しました</div>
        <div>
          <div class="mb-2">
            <span
              class="mx-2 inline-flex items-center rounded-md bg-gray-100 px-2.5 py-0.5 text-sm font-medium text-gray-800"
              >案件名</span
            >
            {{ result.title }}
          </div>
          <div class="mb-2">
            <span
              class="mx-2 inline-flex items-center rounded-md bg-gray-100 px-2.5 py-0.5 text-sm font-medium text-gray-800"
              >作業日</span
            >
            {{ displayedResult.work_date }}
          </div>
          <div class="mb-2">
            <span
              class="mx-2 inline-flex items-center rounded-md bg-gray-100 px-2.5 py-0.5 text-sm font-medium text-gray-800"
              >作業予定開始時間</span
            >
            {{ displayedResult.scheduled_start_time }}
          </div>
          <div class="mb-2">
            <span
              class="mx-2 inline-flex items-center rounded-md bg-gray-100 px-2.5 py-0.5 text-sm font-medium text-gray-800"
              >作業予定終了時間</span
            >
            {{ displayedResult.scheduled_end_time }}
          </div>
          <div class="mb-2">
            <span
              class="mx-2 inline-flex items-center rounded-md bg-gray-100 px-2.5 py-0.5 text-sm font-medium text-gray-800"
              >税率</span
            >
            {{ result.tax }}%
          </div>
          <div class="mb-2">
            <span
              class="mx-2 inline-flex items-center rounded-md bg-gray-100 px-2.5 py-0.5 text-sm font-medium text-gray-800"
              >金額</span
            >
            {{ result.price }}
          </div>
        </div>
      </div>
      <template #footer>
        <button
          v-if="!displayResult"
          :disabled="!isSubmitActive"
          class="ml-6 rounded-md border border-transparent bg-yellow-500 px-8 py-1.5 font-medium text-white shadow-sm hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:ring-offset-2"
          :class="{ 'cursor-not-allowed opacity-50': !isSubmitActive }"
          @click="submit"
        >
          登録
        </button>
        <button
          v-if="displayResult"
          class="ml-6 rounded-md border border-transparent bg-yellow-500 px-8 py-1.5 font-medium text-white shadow-sm hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:ring-offset-2"
          @click="closeAndReset"
        >
          OK
        </button>
      </template>
    </Dialog>
  </div>
</template>
